import {
  computed,
  reactive,
  type ComputedRef,
  type UnwrapNestedRefs,
} from 'vue';

import {
  API_GET_ACCOUNTS,
  API_POST_SET_ACCOUNT,
  SET_STATE_ACCOUNT,
  SET_STATE_ACCOUNTS,
} from '../store/types';

import { store } from '@/store';
import { useUserService } from '@/modules/User/services/UserService';
import type { Account, UserRole } from '@/modules/Accounts/types';

import { adminAccountTypes } from '@/config';

export interface AccountsStore {
  account: ComputedRef<Account>;
  accounts: ComputedRef<Account[]>;
  isFetching: ComputedRef<boolean>;
  isOwner: ComputedRef<boolean>;
  isOwnerOrAdmin: ComputedRef<boolean>;
  isOperator: ComputedRef<boolean>;
  isAnalyst: ComputedRef<boolean>;
  userHasMultipleAccounts: ComputedRef<number>;
  getUserAccounts: (userId?: string) => Promise<Account[]>;
  selectUserAccount: (account: Account) => Promise<void>;
  selectDefaultAccount: (userId?: string) => Promise<void>;
  updateAccountRole: (newRole: UserRole) => void;
  isValidAccountSelected: ComputedRef<boolean>;
}

export function useAccountsService(): UnwrapNestedRefs<AccountsStore> {
  const authService = useUserService();
  const isFetching = computed(() => (
    store.state.accounts.get_accounts.isFetching
    || store.state.accounts.post_set_account.isFetching
  ));
  const accounts = computed(() => (store.state?.accounts?.accounts || []) as Account[]);
  const account = computed(() => (store.state?.accounts?.account || {}) as Account);
  const isOwner = computed(() => (
    account.value
    && account.value.user?.team_member?.role === 'account_owner'
  ));
  const isOwnerOrAdmin = computed(() => (
    account.value
    && adminAccountTypes.includes(account.value.user?.team_member?.role as string)
  ));
  const isOperator = computed(() => (
    account.value?.user?.team_member?.role === 'basic'
  ));
  const isAnalyst = computed(() => (
    account.value?.user?.team_member?.role === 'analyst'
  ));
  const userHasMultipleAccounts = computed(() => (accounts.value?.length || 0));
  const isValidAccountSelected = computed(() => (
    !!account.value?.id
    && !!account.value.user?.active_properties_count
  ));

  async function getUserAccounts(userId: string = authService.user?.id ?? ''): Promise<Account[]> {
    const { data } = await store.dispatch(
      API_GET_ACCOUNTS.REQUEST,
      {
        params: {
          id: userId,
        },
      },
    );
    const accountsWithActiveProperties = data
      .filter((item: Account) => item.user.active_properties_count > 0);
    store.commit(SET_STATE_ACCOUNTS, accountsWithActiveProperties);
    return accountsWithActiveProperties;
  }

  async function selectUserAccount(selectedAccount: Account) {
    await store.dispatch(
      API_POST_SET_ACCOUNT.REQUEST,
      {
        data: {
          account_id: selectedAccount.id,
        },
      },
    );

    store.commit(SET_STATE_ACCOUNT, selectedAccount);
  }

  async function selectDefaultAccount(userId?: string) {
    await getUserAccounts(userId);

    if (accounts.value.length === 1) {
      await selectUserAccount(accounts.value[0]);
      return;
    }

    const defaultAccount = accounts.value.find(item => item.default);
    if (defaultAccount) {
      await selectUserAccount(defaultAccount);
    }
  }

  /**
   * Optimistic update to change the current account's role locally.
   * Useful for immediate UI updates after actions like transferring ownership.
   */
  function updateAccountRole(newRole: UserRole) {
    const currentAccount = account.value;
    if (!currentAccount?.user?.team_member) return;

    const updatedAccount: Account = {
      ...currentAccount,
      user: {
        ...currentAccount.user,
        team_member: {
          ...currentAccount.user.team_member,
          role: newRole,
        },
      },
    };

    store.commit(SET_STATE_ACCOUNT, updatedAccount);
  }

  return reactive({
    account,
    accounts,
    isFetching,
    isOwnerOrAdmin,
    isOwner,
    isOperator,
    isAnalyst,
    getUserAccounts,
    selectUserAccount,
    selectDefaultAccount,
    updateAccountRole,
    isValidAccountSelected,
    userHasMultipleAccounts,
  });
}
